/** 質問タイプ */
export type QuestionType = 'SINGLE_SELECT' | 'MULTI_SELECT' | 'CONDITIONAL_SELECT';

/** 選択肢 */
export interface SelectOption {
  id: string;
  label: string;
}

/** 基本の質問定義 */
interface BaseQuestionDef {
  id: string;
  label: string;
  description?: string;
  required?: boolean;
}

/** 単一選択 */
export interface SingleSelectDef extends BaseQuestionDef {
  type: 'SINGLE_SELECT';
  options: readonly SelectOption[];
}

/** 複数選択 */
export interface MultiSelectDef extends BaseQuestionDef {
  type: 'MULTI_SELECT';
  options: readonly SelectOption[];
}

/**
 * 条件付き追加選択のルール
 * primaryOptions のうち特定の値が選択された場合に、追加の選択肢を表示する
 */
export interface ConditionalRule {
  triggerValue: string;
  secondaryOptions: readonly SelectOption[];
  secondaryLabel?: string;
}

/** 条件付き選択（各アイテムに対して選択 + 条件で追加選択が出現） */
export interface ConditionalSelectDef extends BaseQuestionDef {
  type: 'CONDITIONAL_SELECT';
  items: readonly SelectOption[];
  primaryOptions: readonly SelectOption[];
  conditionalRules: ConditionalRule[];
}

export type QuestionDef = SingleSelectDef | MultiSelectDef | ConditionalSelectDef;

/** ステップ定義 */
export interface StepDef {
  id: string;
  title: string;
  questions: QuestionDef[];
}

/** フォーム全体の定義 */
export interface FormSchemaDef {
  version: string;
  steps: StepDef[];
}

/** question type → answer 型のマップ */
export type AnswerMap = {
  SINGLE_SELECT: { selectedId: string };
  MULTI_SELECT: { selectedIds: string[] };
  CONDITIONAL_SELECT: {
    selections: Record<string, string>;
    secondarySelections: Record<string, Record<string, string>>;
  };
};

/** 回答値（内部管理用） */
export type AnswerValue = AnswerMap[QuestionType];

/** 全回答の状態 */
export type FormAnswers = Record<string, AnswerValue>;

/** question.type と answer を連動させた discriminated union */
export type ResolvedField =
  | { type: 'SINGLE_SELECT'; question: SingleSelectDef; selectedId: string }
  | { type: 'MULTI_SELECT'; question: MultiSelectDef; selectedIds: string[] }
  | {
      type: 'CONDITIONAL_SELECT';
      question: ConditionalSelectDef;
      selections: Record<string, string>;
      secondarySelections: Record<string, Record<string, string>>;
    };

/** question.type に基づいて answer のデフォルト値を返す */
const DEFAULT_ANSWERS: { [K in QuestionType]: AnswerMap[K] } = {
  SINGLE_SELECT: { selectedId: '' },
  MULTI_SELECT: { selectedIds: [] },
  CONDITIONAL_SELECT: { selections: {}, secondarySelections: {} },
};

/** Record<string, AnswerValue> と QuestionDef の型境界を吸収する */
export function resolveField(question: QuestionDef, answer: AnswerValue | undefined): ResolvedField {
  switch (question.type) {
    case 'SINGLE_SELECT':
      return {
        type: 'SINGLE_SELECT',
        question,
        selectedId: (answer as AnswerMap['SINGLE_SELECT'] | undefined)?.selectedId
          ?? DEFAULT_ANSWERS.SINGLE_SELECT.selectedId,
      };
    case 'MULTI_SELECT':
      return {
        type: 'MULTI_SELECT',
        question,
        selectedIds: (answer as AnswerMap['MULTI_SELECT'] | undefined)?.selectedIds
          ?? DEFAULT_ANSWERS.MULTI_SELECT.selectedIds,
      };
    case 'CONDITIONAL_SELECT': {
      const typed = answer as AnswerMap['CONDITIONAL_SELECT'] | undefined;
      return {
        type: 'CONDITIONAL_SELECT',
        question,
        selections: typed?.selections ?? DEFAULT_ANSWERS.CONDITIONAL_SELECT.selections,
        secondarySelections: typed?.secondarySelections ?? DEFAULT_ANSWERS.CONDITIONAL_SELECT.secondarySelections,
      };
    }
  }
}
